import React from 'react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Activity, Moon, Sun, ArrowLeft, FileText, Scale, Shield,
  CreditCard, Video, UserCheck, AlertTriangle, Gavel, Users,
  Lock, Globe, Brain, Stethoscope, Heart, Clock, Phone
} from 'lucide-react';

export default function TermsPage() {
  const { theme, toggleTheme } = useTheme();
  const effectiveDate = 'February 13, 2025';

  return (
    <div className="min-h-screen bg-gradient-to-b from-cn-bone-100 via-white to-cn-bone-200 dark:from-cn-ink-950 dark:via-cn-ink-900 dark:to-cn-ink-950" data-testid="terms-page">
      <Seo
        title="Terms of Service | CaraLoom"
        description="CaraLoom's Terms of Service — our role as a healthcare technology marketplace, eligibility, payments, telehealth, liability, and dispute resolution."
        path="/terms"
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Terms', path: '/terms' }])]}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="pt-12 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-cn-primary dark:text-blue-400 hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Scale className="w-8 h-8 text-cn-primary dark:text-blue-400" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white font-display mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Effective Date: {effectiveDate}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Platform Role */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  1. Platform Role
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-600 dark:text-slate-400 mb-4">CaraLoom is a technology platform that:</p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2 mb-4">
                  <li>Facilitates connections between families/patients and independent licensed clinicians</li>
                  <li>Does not provide direct medical care</li>
                  <li>Does not guarantee clinical outcomes</li>
                </ul>
                <p className="text-slate-600 dark:text-slate-400 font-medium">
                  Clinicians are independent professionals responsible for their own services.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Eligibility */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  2. Eligibility
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-700 dark:text-slate-300 font-medium mb-2">Users must:</p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2 mb-4">
                  <li>Be at least 18 years old</li>
                  <li>Provide accurate information</li>
                  <li>Comply with all applicable laws</li>
                </ul>
                <p className="text-slate-700 dark:text-slate-300 font-medium mb-2">Clinicians must:</p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
                  <li>Maintain active licensure</li>
                  <li>Carry required insurance</li>
                  <li>Complete verification processes</li>
                  <li>Provide truthful credentials</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Emergency Warning */}
          <Card className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-red-800 dark:text-red-200 font-display mb-2">
                    3. No Emergency Use
                  </h2>
                  <p className="text-red-700 dark:text-red-300 font-medium">
                    CaraLoom is not intended for emergency medical services.
                  </p>
                  <p className="text-red-600 dark:text-red-400 mt-2">
                    In case of emergency, dial 911 or seek immediate medical attention.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payments */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  4. Payments &amp; Verified Settlement
                </h2>
              </div>
              <div className="pl-16 space-y-4">
                <p className="text-slate-700 dark:text-slate-300 font-semibold">
                  CaraLoom is a software platform and not a bank, escrow agent, or money transmitter.
                  We utilize third-party payment processors to facilitate direct clinician-to-family
                  transactions based on verified service-delivery milestones.
                </p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
                  <li>
                    Payment processing is conducted through third-party processors
                    (Stripe and Stripe Connect). CaraLoom does not take custody of customer funds.
                  </li>
                  <li>
                    Funds are <strong>secured up-front</strong> at the time of booking and
                    <strong> scheduled for payout</strong> to the clinician once the documented
                    clinical visit is signed off in the clinician&apos;s SOAP note
                    (the &ldquo;verified-settlement&rdquo; workflow).
                  </li>
                  <li>
                    CaraLoom charges a flat 12% software service rate per completed booking,
                    plus per-active-patient or per-seat subscription fees where applicable to
                    facility, provider, payer, or enterprise customers. The service rate covers
                    software, verification, matching, payment processing, insurance, credentialing,
                    and trust &amp; safety operations &mdash; it is not a money-transmission fee.
                  </li>
                  <li>
                    Refund and cancellation policies are presented at the time of booking. Any
                    refunds are issued by the third-party payment processor.
                  </li>
                  <li>
                    Clinicians acknowledge they are independent professionals contracting directly
                    with patients; CaraLoom does not employ clinicians and is not a party to the
                    clinician-patient agreement.
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Telehealth Use */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Video className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  5. Telehealth Use
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-600 dark:text-slate-400 mb-4">Users acknowledge:</p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
                  <li>Telehealth may have technological limitations</li>
                  <li>Recording sessions without consent is prohibited</li>
                  <li>AI-generated documentation is a support tool only</li>
                  <li>Telehealth does not replace emergency medical services</li>
                  <li>Clinicians are responsible for confirming patient identity and location prior to each session</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* In-Person Care */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <UserCheck className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  6. In-Person Care
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-600 dark:text-slate-400 mb-4">If in-person services are selected:</p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
                  <li>Background checks must be completed</li>
                  <li>Platform safety tools must be followed</li>
                  <li>CaraLoom is not liable for independent clinician actions beyond platform verification</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  7. Limitation of Liability
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-600 dark:text-slate-400 mb-4">CaraLoom shall not be liable for:</p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2 mb-4">
                  <li>Clinical decisions made by independent providers</li>
                  <li>Misuse of the platform</li>
                  <li>Service interruptions</li>
                  <li>Indirect or consequential damages</li>
                </ul>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Maximum liability shall not exceed fees paid to the platform in the preceding six months.
                </p>
                <div className="space-y-3 mt-4 p-4 rounded-xl bg-slate-50 dark:bg-cn-ink-700/40 border border-slate-200 dark:border-cn-ink-650 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  <p>
                    <span className="font-semibold text-slate-900 dark:text-white">CaraLoom does not employ any clinician and is not responsible for the conduct of any user of our platform.</span> All information in clinician profiles, job posts, applications, reviews, and messages is created by users of our site and is not generated or independently verified by CaraLoom. You must do your own diligence to ensure the clinician or care arrangement you choose is appropriate for your needs and complies with applicable laws.
                  </p>
                  <p>
                    <span className="font-semibold text-slate-900 dark:text-white">Background checks are not always 100% accurate</span>, may not capture an individual's entire criminal or licensure history, and cannot guarantee safety. Verification badges shown on CaraLoom reflect checks performed at the time of clinician onboarding and may not reflect events occurring after that date. Employees, contractors, or sub-contractors of any agency listed on CaraLoom may not be individually background checked.
                  </p>
                  <p>
                    <span className="font-semibold text-slate-900 dark:text-white">Cara and all AI features</span> are informational assistants only and are not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition. In a medical emergency, call 911 immediately.
                  </p>
                  <p>
                    Promotional offers, referral bonuses, and platform discounts are subject to terms and may change at any time. Limited referrals may qualify. Rewards are issued to the email address on file and must be redeemed within the period stated at issuance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Independent Contractor Agreement */}
          <Card className="bg-gradient-to-r from-cn-primary to-cn-sage-500 dark:from-blue-900/20 dark:to-indigo-900/20 border-0">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-white dark:bg-cn-ink-800 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  8. Clinician Independent Contractor Agreement
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-600 dark:text-slate-400 mb-4">By registering as a clinician on CaraLoom, you acknowledge and agree:</p>
                <ol className="list-decimal list-inside text-slate-600 dark:text-slate-400 space-y-3">
                  <li>You are an independent contractor and not an employee, agent, joint venturer, or partner of CaraLoom.</li>
                  <li>You are solely responsible for: clinical decisions, documentation, professional conduct, maintaining active licensure, carrying required professional liability insurance, and compliance with federal and state healthcare regulations.</li>
                  <li>CaraLoom does not: supervise clinical care, direct treatment decisions, or dictate medical protocols.</li>
                  <li>You agree to indemnify and hold harmless CaraLoom from claims arising from your clinical services.</li>
                  <li>You authorize verification of: licensure, certifications, sanction checks, insurance status, and background screening (if required).</li>
                </ol>
                <p className="text-slate-700 dark:text-slate-300 font-medium mt-4">
                  Failure to maintain compliance may result in suspension or removal from the platform.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Arbitration */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gavel className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  9. Arbitration & Dispute Resolution
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Any dispute arising from use of CaraLoom shall be resolved through binding arbitration rather than court litigation, except where prohibited by law.
                </p>
                <ol className="list-decimal list-inside text-slate-600 dark:text-slate-400 space-y-2">
                  <li>Disputes must first be submitted in writing for informal resolution.</li>
                  <li>If unresolved within 30 days, disputes shall be submitted to arbitration under the rules of the American Arbitration Association (AAA).</li>
                  <li>Arbitration will occur in the state of CaraLoom's principal place of business unless otherwise agreed.</li>
                  <li>Users waive the right to participate in class action lawsuits.</li>
                  <li>Each party bears its own legal fees unless determined otherwise by the arbitrator.</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Professional Liability */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  10. Professional Liability Notice
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-700 dark:text-slate-300 font-medium mb-4">
                  CaraLoom is a healthcare technology platform and not a healthcare provider.
                </p>
                <p className="text-slate-600 dark:text-slate-400 mb-4">CaraLoom:</p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2 mb-4">
                  <li>Does not practice medicine or nursing</li>
                  <li>Does not supervise independent clinicians</li>
                  <li>Does not guarantee outcomes</li>
                  <li>Is not responsible for clinical negligence by independent providers</li>
                </ul>
                <p className="text-slate-600 dark:text-slate-400">
                  Patients are encouraged to verify provider licensing independently through state licensing boards.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* User Code of Conduct */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  11. User Code of Conduct
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-600 dark:text-slate-400 mb-4">Users agree not to:</p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-2">
                  <li>Harass or discriminate against clinicians or patients</li>
                  <li>Share platform credentials</li>
                  <li>Use the platform for unlawful purposes</li>
                  <li>Circumvent payment systems</li>
                  <li>Record telehealth sessions without consent</li>
                  <li>Provide false clinical information</li>
                </ul>
                <p className="text-slate-700 dark:text-slate-300 font-medium mt-4">
                  Violation may result in suspension or permanent removal.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Modifications */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display pt-2">
                  12. Modifications
                </h2>
              </div>
              <div className="pl-16">
                <p className="text-slate-600 dark:text-slate-400">
                  We may update these Terms at any time. Continued use of the platform indicates acceptance of changes.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="bg-gradient-to-r from-cn-primary to-cn-sage-500 dark:from-blue-900/20 dark:to-indigo-900/20 border-0">
            <CardContent className="p-6 lg:p-8 text-center">
              <Phone className="w-12 h-12 text-cn-primary dark:text-blue-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display mb-4">
                Questions About Our Terms?
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                If you have any questions about these Terms of Service, please contact us.
              </p>
              <a href="mailto:legal@carematch.ai" className="text-cn-primary dark:text-blue-400 font-medium hover:underline">
                legal@carematch.ai
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-900">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500 dark:text-slate-400">
          <p>© {new Date().getFullYear()} CaraLoom. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
